import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import usa from "../../src/assets/images/usa.png"
import nl from "../../src/assets/images/nl.png"
import backgroundIMG from "../../src/assets/images/background.png"
import logo from "../../src/assets/images/logo.png"

import Header from '../components/Header'
import Footer from '../components/Footer'
import { useAuth } from "../context/AuthContext"

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api'

const REFRESH_EVERY = 60
const BAR_COUNT = 60

function nowString() {
  const d = new Date()
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const pad = n => String(n).padStart(2, '0')
  return `${months[d.getMonth()]} ${d.getDate()} at ${pad(d.getHours())}:${pad(d.getMinutes())} (local)`
}

function flagForService(svc) {
  if (svc.flag === 'us' || svc.region === 'us') return usa
  if (svc.flag === 'nl' || svc.region === 'nl') return nl
  return null
}

function generateBarsFromUptime(uptime, placeholder) {
  if (placeholder) {
    return Array.from({ length: BAR_COUNT }, () => ({ down: false, muted: true }))
  }
  const pct = Math.min(100, Math.max(0, Number(uptime) || 0)) / 100
  return Array.from({ length: BAR_COUNT }, (_, i) => ({
    down: (i + 1) / BAR_COUNT > pct,
    muted: false,
  }))
}

function UptimeBars({ bars, rowIndex }) {
  return (
    <div className="flex items-center gap-[2px] min-w-0 overflow-hidden flex-1 pr-2">
      {bars.map((bar, i) => (
        <div
          key={i}
          className="flex-shrink-0 rounded-sm"
          style={{
            width: '7px',
            height: '28px',
            backgroundColor: bar.muted
              ? 'rgba(148,163,184,0.35)'
              : bar.down
                ? '#ef4444'
                : '#60C94F',
            animation: `barIn 0.4s ease both`,
            animationDelay: `${rowIndex * 0.05 + i * 0.003}s`,
          }}
        />
      ))}
    </div>
  )
}

function ServiceRow({ service, index }) {
  const bars = useMemo(
    () => generateBarsFromUptime(service.uptime, service.placeholder),
    [service.uptime, service.placeholder, service.id]
  )
  const flagImg = flagForService(service)

  return (
    <div
      className="bg-[#FFFFFF21] border border-[#EBEBEB47] rounded-xl px-4 py-3
                 flex items-center gap-2 hover:bg-[#1a2236] transition-all duration-200"
      style={{
        animation: `rowIn 0.5s ease both`,
        animationDelay: `${index * 0.06 + 0.04}s`,
      }}
    >
      <div className="flex items-center gap-2 font-geist whitespace-nowrap w-[110px] sm:w-[140px] flex-shrink-0">
        <span
          className="w-[22px] h-[22px] flex items-center justify-center rounded-full flex-shrink-0"
          style={{
            backgroundColor: service.placeholder ? 'rgba(148,163,184,0.4)' : service.up ? '#60C94F' : '#ef4444',
          }}
        >
          {service.up ? (
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.00009 6.07873L5.33806 11.6628L12.7243 1.00012" stroke="#242534" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg width="10" height="10" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 2L12 12M12 2L2 12" stroke="#242534" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          )}
        </span>
        <span className="text-sm text-white">{service.name}</span>
        {flagImg ? (
          <img
            src={flagImg}
            alt={service.name}
            className="w-[20px] h-[20px] rounded-full object-cover flex-shrink-0"
          />
        ) : null}
      </div>

      <span
        className="font-geist text-xs sm:text-sm whitespace-nowrap w-[58px] flex-shrink-0"
        style={{ color: service.placeholder ? 'rgba(226,232,240,0.7)' : service.up ? '#60C94F' : '#ef4444' }}
      >
        {service.placeholder ? '—' : `${Number(service.uptime).toFixed(2)}%`}
      </span>

      <UptimeBars bars={bars} rowIndex={index} />

      <div
        className="flex items-center justify-end gap-1.5 text-xs sm:text-sm font-geist whitespace-nowrap flex-shrink-0 w-[48px]"
        style={{ color: service.placeholder ? 'rgba(226,232,240,0.6)' : service.up ? '#60C94F' : '#ef4444' }}
      >
        {!service.placeholder && (
          <span
            className="w-[10px] h-[10px] sm:w-[13px] sm:h-[13px] rounded-full animate-pulse flex-shrink-0"
            style={service.up
              ? { backgroundColor: '#60C94F', boxShadow: '0 0 6px #60C94F' }
              : { backgroundColor: '#ef4444' }
            }
          />
        )}
        {service.placeholder ? '—' : service.up ? 'UP' : 'DOWN'}
      </div>
    </div>
  )
}

function CheckIcon({ overall }) {
  const ok = overall === 'operational'
  const mid = overall === 'degraded'
  return (
    <div
      className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0"
      style={{
        backgroundColor: ok ? '#60C94F' : mid ? '#f59e0b' : '#ef4444',
        boxShadow: ok
          ? '0 0 20px rgba(96,201,79,0.3), 0 0 40px rgba(96,201,79,0.15)'
          : mid
            ? '0 0 20px rgba(245,158,11,0.35)'
            : '0 0 20px rgba(239,68,68,0.35)',
        animation: 'pulseGlow 2.5s ease-in-out infinite',
      }}
    >
      {ok ? (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none"
             stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : mid ? (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none"
             stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 9v4M12 17h.01" />
          <circle cx="12" cy="12" r="10" />
        </svg>
      ) : (
        <svg className="w-4 h-4 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none"
             stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      )}
    </div>
  )
}

export default function Status() {
  const { user } = useAuth()
  const [services, setServices] = useState([])
  const [overall, setOverall] = useState('operational')
  const [providerConfigured, setProviderConfigured] = useState(true)
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(null)
  const [countdown, setCountdown] = useState(REFRESH_EVERY)
  const [lastUpdated, setLastUpdated] = useState(nowString)
  const mainRef = useRef(null)

  const loadStatus = useCallback(async () => {
    try {
      setFetchError(null)
      const res = await fetch(`${API_URL}/status`, { credentials: 'omit' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      setServices(Array.isArray(data.services) ? data.services : [])
      setOverall(data.overall || 'operational')
      const pc = data.providerConfigured ?? data.acerdpConfigured
      setProviderConfigured(pc !== false)
      setAlerts(Array.isArray(data.alerts) ? data.alerts : [])
      setLastUpdated(nowString())
    } catch (e) {
      setFetchError(e.message || 'Failed to load status')
      setAlerts([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  useEffect(() => {
    const raf = window.requestAnimationFrame(() => {
      mainRef.current?.scrollIntoView({ behavior: 'auto', block: 'center' })
    })
    return () => window.cancelAnimationFrame(raf)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          loadStatus()
          return REFRESH_EVERY
        }
        return c - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [loadStatus])

  const headline = useMemo(() => {
    if (overall === 'operational') return { main: 'All Systems', sub: 'Operational', color: '#60C94F' }
    if (overall === 'degraded') return { main: 'Some Systems', sub: 'Degraded', color: '#f59e0b' }
    return { main: 'Service', sub: 'Disruption', color: '#ef4444' }
  }, [overall])

  return (
    <>
      <style>{`
        @keyframes rowIn {
            from { opacity: 0; transform: translateY(8px); }
            to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes barIn {
            from { transform: scaleY(0); opacity: 0; }
            to   { transform: scaleY(1); opacity: 1; }
            }
            @keyframes pulseGlow {
                0%, 100% { box-shadow: 0 0 20px rgba(96,201,79,0.3), 0 0 40px rgba(96,201,79,0.15); }
                50%       { box-shadow: 0 0 30px rgba(96,201,79,0.5), 0 0 60px rgba(96,201,79,0.25); }
                }
                `}</style>

        <Header user={user} logo={logo} sticky={false} />
        <div
        className="min-h-screen text-slate-100 flex flex-col items-center justify-center p-4 sm:p-6"
        style={{
            backgroundImage: `url(${backgroundIMG})`,
            backgroundPosition: 'bottom center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
        }}
        >
        <div ref={mainRef} className="w-full max-w-5xl">

          <div className="text-center mb-6 sm:mb-8">
            <div className="flex items-center font-poppins justify-center gap-3 sm:gap-3.5 mb-2">
              <CheckIcon overall={overall} />
              <h1 className="text-2xl sm:text-3xl md:text-5xl tracking-tight">
                <span style={{
                  background: 'linear-gradient(90deg, #FFFFFF66, #FFFFFF, #FFFFFF66)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  {headline.main}
                </span>{' '}
                <span style={{ color: headline.color }}>{headline.sub}</span>
              </h1>
            </div>
            <p className="text-white text-xs sm:text-sm mt-3 sm:mt-4">
              Last updated on{' '}
              <span className="text-slate-400 font-medium">{lastUpdated}</span>
              {' '}| Next refresh in{' '}
              <span className="text-slate-400 font-medium">{countdown}s</span>
            </p>
            {!providerConfigured && (
              <p className="text-amber-200/90 text-xs sm:text-sm mt-2 max-w-xl mx-auto">
                USA and NL region status will show live data once infrastructure monitoring is enabled on the server.
              </p>
            )}
          </div>

          {fetchError && (
            <div className="mb-4 rounded-xl border border-red-500/40 bg-red-950/40 px-4 py-3 text-sm text-red-100 flex flex-wrap items-center justify-between gap-2">
              <span>{fetchError}</span>
              <button
                type="button"
                onClick={() => { setLoading(true); loadStatus(); }}
                className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-medium hover:bg-white/20"
              >
                Retry
              </button>
            </div>
          )}

          <div className="flex flex-col gap-1.5 mb-2 min-h-[120px]">
            {loading && services.length === 0 ? (
              <div className="flex justify-center py-12">
                <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500/30 border-t-blue-500" aria-hidden />
              </div>
            ) : (
              services.map((svc, i) => (
                <ServiceRow key={svc.id || i} service={svc} index={i} />
              ))
            )}
          </div>

          {!loading && alerts.length > 0 && (
            <div
              className="bg-[#FFFFFF21] border border-[#EBEBEB47] mt-4 sm:mt-5 rounded-xl p-4 sm:p-5"
              style={{ animation: 'rowIn 0.5s ease 0.28s both' }}
            >
              <h2 className="text-white text-sm sm:text-base font-geist tracking-widest mb-3">
                Active notices
              </h2>
              <ul className="space-y-3">
                {alerts.map((a) => {
                  const sev = a.severity === 'warning' ? 'warning' : 'error'
                  return (
                    <li
                      key={a.id}
                      className={`rounded-lg border px-3 py-2.5 text-xs sm:text-sm font-poppins ${
                        sev === 'warning'
                          ? 'border-amber-500/35 bg-amber-950/25 text-amber-50/95'
                          : 'border-red-500/35 bg-red-950/25 text-red-50/95'
                      }`}
                    >
                      <div className="font-semibold text-white/95">{a.title}</div>
                      <div className="mt-1 text-white/80 leading-relaxed">{a.message}</div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

        </div>
      </div>
      <Footer/>
    </>
  )
}
